import React from 'react';
import { AbsoluteFill, Img, interpolate, useCurrentFrame, spring, useVideoConfig, staticFile } from 'remotion';

export interface CtaOverlayProps {
  imageSrc: string;  // Brand image
  secondaryImageSrc: string;  // Product image
  headline: string;
  subheadline?: string;
  buttonText?: string;
  variation: 1 | 2 | 3 | 4 | 5 | 6;
  width: number;
  height: number;
  onButtonClick?: () => void;
  productAnimation?: 'prod' | 'flip' | 'rotate' | 'shake' | 'fromBottom' | 'fromTop' | 'meetCenter' | 'slowBottom';
  ctaText?: string;
}

const theme = {
  background: 'rgba(0,0,0,0.85)',
  accent: '#E53935',
  text: '#fff',
  buttonText: '#fff',
  buttonBg: '#E53935',
  buttonShadow: '0 4px 16px rgba(229,57,53,0.3)',
  yellow: '#FFD600',
};

const baseStyles: React.CSSProperties = {
  borderRadius: 18,
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 600,
  width: '100%',
  background: theme.background,
};

const buttonStyles: React.CSSProperties = {
  marginTop: 18,
  background: theme.buttonBg,
  color: theme.buttonText,
  border: 'none',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 22,
  padding: '12px 32px',
  cursor: 'pointer',
  boxShadow: theme.buttonShadow,
  textTransform: 'uppercase',
  letterSpacing: 1,
};

const imageStyles: React.CSSProperties = {
  width: 110,
  height: 110,
  objectFit: 'contain',
  marginBottom: 18,
  marginTop: 8,
};

const headlineStyles: React.CSSProperties = {
  color: theme.text,
  fontWeight: 900,
  fontSize: 32,
  textAlign: 'center',
  marginBottom: 8,
  textTransform: 'uppercase',
  letterSpacing: 1,
  lineHeight: 1.1,
};

const subheadlineStyles: React.CSSProperties = {
  color: theme.text,
  fontWeight: 400,
  fontSize: 20,
  textAlign: 'center',
  marginBottom: 12,
  opacity: 0.85,
};

// Animation helpers
const useFromTop = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [-80, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useFromBottom = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [80, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useSwipeUp = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [40, -10], { extrapolateRight: 'clamp' })}px)`
  };
};

const useBounce = (duration = 30) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const scale = spring({ frame, fps, config: { damping: 8, mass: 0.7, stiffness: 120 } });
  return {
    opacity: Math.min(1, frame / duration),
    transform: `scale(${interpolate(scale, [0, 0.7, 0.9, 1], [0.5, 1.1, 0.95, 1])})`,
  };
};

const useSlideIn = (from: 'left' | 'right', duration = 30) => {
  const frame = useCurrentFrame();
  const start = from === 'left' ? -200 : 200;
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateX(${interpolate(frame, [0, duration], [start, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useSlideInFrom = (direction: 'left' | 'right' | 'top' | 'bottom', duration = 30, delay = 0) => {
  const frame = useCurrentFrame() - delay;
  let start: number;
  let prop: 'translateX' | 'translateY';
  if (direction === 'left') {
    start = -200;
    prop = 'translateX';
  } else if (direction === 'right') {
    start = 200;
    prop = 'translateX';
  } else if (direction === 'top') {
    start = -120;
    prop = 'translateY';
  } else {
    start = 120;
    prop = 'translateY';
  }
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `${prop}(${interpolate(frame, [0, duration], [start, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useFadeIn = (duration = 30, delay = 0) => {
  const frame = useCurrentFrame() - delay;
  return {
    opacity: interpolate(frame, [0, duration], [0, 1], { extrapolateRight: 'clamp' }),
    transform: 'none',
  };
};

const useRotateIn = (duration = 30, delay = 0) => {
  const frame = useCurrentFrame() - delay;
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `rotate(${interpolate(frame, [0, duration], [-90, 0], { extrapolateRight: 'clamp' })}deg)`
  };
};

// New animation helpers for product movement
const useProductFromBottom = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [200, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useProductFromTop = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [-200, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useProductMeetCenter = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [100, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useProductSlowBottom = (duration = 45) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [300, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

// New animation helpers for product and text movement
const useTextFromBottom = (duration = 30, delay = 0) => {
  const frame = useCurrentFrame() - delay;
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [100, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

const useMeetCenter = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `translateY(${interpolate(frame, [0, duration], [50, 0], { extrapolateRight: 'clamp' })}px)`
  };
};

// Update useProductAnimation to include new animations
const useProductAnimation = (type: string, duration = 30) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  // Pre-calculate all animation values
  const fromBottomAnim = useProductFromBottom(duration);
  const fromTopAnim = useProductFromTop(duration);
  const meetCenterAnim = useProductMeetCenter(duration);
  const slowBottomAnim = useProductSlowBottom(duration);
  
  let progress, rotateProgress, shakeProgress, shake, scale;
  
  switch (type) {
    case 'fromBottom':
      return fromBottomAnim;
    case 'fromTop':
      return fromTopAnim;
    case 'meetCenter':
      return meetCenterAnim;
    case 'slowBottom':
      return slowBottomAnim;
    case 'flip':
      progress = interpolate(frame, [0, duration], [0, 1], { extrapolateRight: 'clamp' });
      return {
        opacity: progress,
        transform: `rotateY(${interpolate(progress, [0, 1], [90, 0])}deg) scale(${interpolate(progress, [0, 1], [0.7, 1])})`,
      };
    case 'rotate':
      rotateProgress = interpolate(frame, [0, duration], [0, 1], { extrapolateRight: 'clamp' });
      return {
        opacity: rotateProgress,
        transform: `rotate(${interpolate(rotateProgress, [0, 1], [-90, 0])}deg) scale(${interpolate(rotateProgress, [0, 1], [0.7, 1])})`,
      };
    case 'shake':
      shakeProgress = interpolate(frame, [0, duration], [0, 1], { extrapolateRight: 'clamp' });
      shake = Math.sin(frame * 0.5) * (1 - shakeProgress) * 10;
      return {
        opacity: shakeProgress,
        transform: `translateX(${shake}px) scale(${interpolate(shakeProgress, [0, 1], [0.7, 1])})`,
      };
    default: // 'prod' (pop in)
      scale = spring({ frame, fps, config: { damping: 80, stiffness: 120 } });
      return {
        opacity: interpolate(frame, [0, duration], [0, 1], { extrapolateRight: 'clamp' }),
        transform: `scale(${interpolate(scale, [0, 1], [0.7, 1])})`,
      };
  }
};

// Responsive sizes based on width/height
const getResponsiveImageSize = (width: number, height: number) => {
  // Use 30% of the smaller dimension, clamp between 120 and 260
  const base = Math.min(width, height);
  return Math.max(120, Math.min(0.3 * base, 260));
};
const getResponsiveHeadlineSize = (width: number) => Math.max(36, Math.min(width * 0.07, 60));
const getResponsiveSubheadlineSize = (width: number) => Math.max(22, Math.min(width * 0.045, 36));
const getResponsiveCtaSize = (width: number) => Math.max(26, Math.min(width * 0.06, 44));

const vibrantButtonStyles: React.CSSProperties = {
  marginTop: 18,
  background: 'linear-gradient(90deg, #FF512F 0%, #DD2476 100%)',
  color: theme.buttonText,
  border: 'none',
  borderRadius: 12,
  fontWeight: 900,
  fontSize: 22,
  padding: '16px 40px',
  cursor: 'pointer',
  boxShadow: '0 8px 32px 0 rgba(255, 81, 47, 0.25), 0 2px 8px 0 rgba(0,0,0,0.18)',
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  transition: 'transform 0.1s',
  outline: '3px solid #fff',
  outlineOffset: '-4px',
  fontFamily: 'Poppins, sans-serif',
};

const vibrantImageStyles = (size: number, isBrand = false) => ({
  width: size,
  height: size,
  objectFit: 'contain',
  marginBottom: 18,
  marginTop: 8,
  borderRadius: isBrand ? '50%' : 18,
  border: '4px solid #fff',
  boxShadow: '0 8px 32px 0 rgba(255,255,255,0.18), 0 2px 8px 0 rgba(0,0,0,0.18)',
  background: 'rgba(255,255,255,0.04)',
});

const vibrantHeadlineStyles = (fontSize: number) => ({
  color: '#fff',
  fontWeight: 900,
  fontSize,
  textAlign: 'center',
  marginBottom: 8,
  textTransform: 'uppercase',
  letterSpacing: 1.5,
  lineHeight: 1.1,
  textShadow: '0 2px 8px rgba(0,0,0,0.45), 0 1px 0 #fff',
  fontFamily: 'Poppins, sans-serif',
});

const vibrantSubheadlineStyles = (fontSize: number) => ({
  color: '#fff',
  fontWeight: 500,
  fontSize,
  textAlign: 'center',
  marginBottom: 12,
  opacity: 0.92,
  textShadow: '0 2px 8px rgba(0,0,0,0.35)',
  fontFamily: 'Poppins, sans-serif',
});

const vibrantCtaTextStyles = (fontSize: number, color: string) => ({
  fontWeight: 900,
  color,
  fontSize,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  textShadow: '0 2px 12px rgba(255,255,0,0.45), 0 1px 0 #fff',
  letterSpacing: 1.5,
  fontFamily: 'Poppins, sans-serif',
});

// Animate card background opacity in
const useCardBgOpacity = (duration = 20) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration], [0, 1], { extrapolateRight: 'clamp' }),
  };
};

// New animation for brand image
const useBrandImageAnimation = (duration = 30) => {
  const frame = useCurrentFrame();
  return {
    opacity: interpolate(frame, [0, duration / 2], [0, 1], { extrapolateRight: 'clamp' }),
    transform: `scale(${interpolate(frame, [0, duration], [0.8, 1], { extrapolateRight: 'clamp' })})`,
  };
};

// --- Main component ---
export const CtaOverlay: React.FC<CtaOverlayProps> = ({
  imageSrc,
  secondaryImageSrc,
  headline,
  subheadline,
  buttonText,
  variation,
  width,
  height,
  onButtonClick,
  productAnimation = 'prod',
  ctaText,
}) => {
  const { fps, durationInFrames, width: videoWidth, height: videoHeight } = useVideoConfig();
  
  // Detect aspect ratio
  const aspectRatio = videoWidth / videoHeight;
  const isPortrait = aspectRatio < 0.8; // 9:16 and similar
  const isSquare = aspectRatio >= 0.8 && aspectRatio <= 1.2; // 1:1 and similar
  const isLandscape = aspectRatio > 1.2; // 16:9 and similar

  // Responsive sizes based on aspect ratio
  const getResponsiveSizes = () => {
    if (isPortrait) {
      // Original sizes for portrait
      return {
        brandImageSize: getResponsiveImageSize(videoWidth, videoHeight) * 0.8,
        productImageSize: getResponsiveImageSize(videoWidth, videoHeight),
        headlineFontSize: getResponsiveHeadlineSize(videoWidth),
        subheadlineFontSize: getResponsiveSubheadlineSize(videoWidth),
        ctaFontSize: getResponsiveCtaSize(videoWidth),
        containerPadding: 32,
        spacing: 24,
      };
    } else if (isSquare) {
      // Increased sizes for square
      return {
        brandImageSize: Math.min(videoWidth, videoHeight) * 0.18,
        productImageSize: Math.min(videoWidth, videoHeight) * 0.22,
        headlineFontSize: Math.min(videoWidth, videoHeight) * 0.055,
        subheadlineFontSize: Math.min(videoWidth, videoHeight) * 0.038,
        ctaFontSize: Math.min(videoWidth, videoHeight) * 0.048,
        containerPadding: 32,
        spacing: 20,
      };
    } else {
      // Increased sizes for landscape
      return {
        brandImageSize: videoHeight * 0.25,
        productImageSize: videoHeight * 0.3,
        headlineFontSize: videoHeight * 0.08,
        subheadlineFontSize: videoHeight * 0.055,
        ctaFontSize: videoHeight * 0.065,
        containerPadding: 24,
        spacing: 18,
      };
    }
  };

  const sizes = getResponsiveSizes();

  // Pre-calculate all animation values
  const brandImageAnim = useBrandImageAnimation();
  const productFromBottom = useProductFromBottom();
  const productFromTop = useProductFromTop();
  const productMeetCenter = useMeetCenter();
  const textFromBottomHeadline = useTextFromBottom(30, 5);
  const textFromBottomSubheadline = useTextFromBottom(30, 10);
  const cardBgOpacity = useCardBgOpacity();

  // Get the appropriate product animation based on variation
  const productAnimationStyle = 
    variation === 1 || variation === 4 ? productFromBottom :
    variation === 2 || variation === 5 ? productFromTop :
    productMeetCenter;

  // Animations for split content
  const fromTop = useFromTop();
  const fromBottom = useFromBottom();
  const swipeUp = useSwipeUp();
  const bounce = useBounce();
  const slideLeft = useSlideIn('left');
  const slideRight = useSlideIn('right');
  const prodAnim = useProductAnimation(productAnimation);
  const frame = useCurrentFrame();

  // Animation hooks for unique variations
  const slideImgLeft = useSlideInFrom('left', 30, 0);
  const slideHeadlineRight = useSlideInFrom('right', 30, 5);
  const slideSubheadlineLeft = useSlideInFrom('left', 30, 10);
  const slideButtonRight = useSlideInFrom('right', 30, 15);
  const fadeHeadlineTop = useSlideInFrom('top', 30, 0);
  const bounceButton = bounce;
  const rotateImg = useRotateIn(30, 0);
  const slideHeadlineUp = useSlideInFrom('bottom', 30, 5);
  const slideButtonLeft = useSlideInFrom('left', 30, 10);

  // --- Variations ---
  let ctaContent: React.ReactNode = null;
  let ctaAnim: React.CSSProperties = fromBottom;
  switch (variation) {
    case 1:
      ctaContent = (
        <button style={{ ...vibrantButtonStyles, fontSize: sizes.ctaFontSize }} onClick={onButtonClick} tabIndex={-1}>{ctaText || 'BUY NOW'}</button>
      );
      break;
    case 2: {
      const jump = Math.abs(Math.sin(frame / 10)) * 16;
      ctaContent = (
        <div style={vibrantCtaTextStyles(sizes.ctaFontSize, theme.yellow)}>
          {ctaText || 'Swipe up'} <span style={{ fontSize: sizes.ctaFontSize * 1.1, display: 'inline-block', transform: `translateY(-2px) translateY(-${jump}px)`, filter: 'drop-shadow(0 0 8px #FFD600)' }}>↑</span>
        </div> 
      );
      ctaAnim = swipeUp;
      break;
    }
    case 3: {
      const clickScale = 1 - 0.2 * (Math.abs(Math.sin(frame / 8)) > 0.8 ? 1 : 0);
      ctaContent = (
        <div style={vibrantCtaTextStyles(sizes.ctaFontSize, theme.yellow)}>
          {ctaText || 'Click here'} <span style={{ fontSize: sizes.ctaFontSize * 1.1, display: 'inline-block', transform: `translateY(-2px) scale(${clickScale})`, filter: 'drop-shadow(0 0 8px #FFD600)' }}>👉</span>
        </div>
      );
      ctaAnim = bounce;
      break;
    }
    case 4:
      ctaContent = (
        <button style={{ ...vibrantButtonStyles, fontSize: sizes.ctaFontSize }} onClick={onButtonClick} tabIndex={-1}>{ctaText || 'LEARN MORE'}</button>
      );
      ctaAnim = slideButtonRight;
      break;
    case 5:
      ctaContent = (
        <button style={{ ...vibrantButtonStyles, fontSize: sizes.ctaFontSize }} onClick={onButtonClick} tabIndex={-1}>{ctaText || 'SHOP NOW'}</button>
      );
      ctaAnim = bounceButton;
      break;
    case 6:
      ctaContent = (
        <button style={{ ...vibrantButtonStyles, fontSize: sizes.ctaFontSize }} onClick={onButtonClick} tabIndex={-1}>{ctaText || 'GET STARTED'}</button>
      );
      ctaAnim = slideButtonLeft;
      break;
    default:
      ctaContent = null;
  }

  // Layout structure based on aspect ratio
  if (isPortrait) {
    // Original vertical layout for portrait
    return (
      <AbsoluteFill style={{ width: '100%', height: '100%', left: 0, top: 0, zIndex: 10000, pointerEvents: 'none' }}>
        <div
          style={{
            position: 'absolute',
            inset: '0',
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.18)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            ...cardBgOpacity,
            zIndex: 1,
            pointerEvents: 'auto',
            borderRadius: 32,
          }}
        />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'auto', padding: sizes.containerPadding }}>
          {/* Brand image */}
          <div style={brandImageAnim}>
            {imageSrc && (
              <Img 
                src={imageSrc.startsWith('http') ? imageSrc : staticFile(imageSrc)} 
                style={vibrantImageStyles(sizes.brandImageSize, true) as React.CSSProperties} 
              />
            )}
          </div>

          {/* Product image */}
          <div style={{ ...productAnimationStyle, marginTop: sizes.spacing }}>
            {secondaryImageSrc && (
              <Img 
                src={secondaryImageSrc.startsWith('http') ? secondaryImageSrc : staticFile(secondaryImageSrc)} 
                style={vibrantImageStyles(sizes.productImageSize) as React.CSSProperties} 
              />
            )}
          </div>

          {/* Headline */}
          <div style={{ ...textFromBottomHeadline, ...vibrantHeadlineStyles(sizes.headlineFontSize) as React.CSSProperties, marginTop: sizes.spacing }}>
            {headline}
          </div>

          {/* Subheadline */}
          {subheadline && (
            <div style={{ ...textFromBottomSubheadline, ...vibrantSubheadlineStyles(sizes.subheadlineFontSize) as React.CSSProperties }}>
              {subheadline}
            </div>
          )}

          {/* CTA */}
          <div style={{ marginTop: sizes.spacing + 8, ...ctaAnim }}>{ctaContent}</div>
        </div>
      </AbsoluteFill>
    );
  } else {
    // Horizontal layout for landscape and square
    return (
      <AbsoluteFill style={{ width: '100%', height: '100%', left: 0, top: 0, zIndex: 10000, pointerEvents: 'none' }}>
        <div
          style={{
            position: 'absolute',
            inset: '0',
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.18)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            ...cardBgOpacity,
            zIndex: 1,
            pointerEvents: 'auto',
            borderRadius: isLandscape ? 24 : 28,
          }}
        />
        <div style={{ 
          position: 'absolute', 
          inset: 0, 
          display: 'flex', 
          flexDirection: isLandscape ? 'row' : 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 2, 
          pointerEvents: 'auto', 
          padding: sizes.containerPadding,
          gap: sizes.spacing
        }}>
          {/* Images section */}
          <div style={{ 
            display: 'flex', 
            flexDirection: isLandscape ? 'column' : 'row', 
            alignItems: 'center', 
            gap: sizes.spacing / 2,
            flex: isLandscape ? '0 0 auto' : '1'
          }}>
            {/* Brand image */}
            <div style={brandImageAnim}>
              {imageSrc && (
                <Img 
                  src={imageSrc.startsWith('http') ? imageSrc : staticFile(imageSrc)} 
                  style={vibrantImageStyles(sizes.brandImageSize, true) as React.CSSProperties} 
                />
              )}
            </div>

            {/* Product image */}
            <div style={productAnimationStyle}>
              {secondaryImageSrc && (
                <Img 
                  src={secondaryImageSrc.startsWith('http') ? secondaryImageSrc : staticFile(secondaryImageSrc)} 
                  style={vibrantImageStyles(sizes.productImageSize) as React.CSSProperties} 
                />
              )}
            </div>
          </div>

          {/* Text and CTA section */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            textAlign: 'center',
            flex: isLandscape ? '1' : '0 0 auto',
            gap: sizes.spacing / 2
          }}>
            {/* Headline */}
            <div style={{ ...textFromBottomHeadline, ...vibrantHeadlineStyles(sizes.headlineFontSize) as React.CSSProperties }}>
              {headline}
            </div>

            {/* Subheadline */}
            {subheadline && (
              <div style={{ ...textFromBottomSubheadline, ...vibrantSubheadlineStyles(sizes.subheadlineFontSize) as React.CSSProperties }}>
                {subheadline}
              </div>
            )}

            {/* CTA */}
            <div style={{ marginTop: sizes.spacing / 2, ...ctaAnim }}>{ctaContent}</div>
          </div>
        </div>
      </AbsoluteFill>
    );
  }
};

export default CtaOverlay; 